import React, {useEffect, useState} from "react";
import {LuCopy, LuCopyCheck} from "react-icons/lu";
import {notification} from "antd";
import {FaCircleCheck} from "react-icons/fa6";

const ExtractLetterOfCreditResults = ({documentId, documentTypePath, extractData}) => {
    const [api, contextHolder] = notification.useNotification();
    const [copiedItem, setCopiedItem] = useState(null);
    const [letterOfCredit, setLetterOfCredit] = useState(null);

    useEffect(() => {
        setLetterOfCredit(extractData[documentId]?.[0] || {})
    }, [extractData, documentId])

    const openNotification = (value) => {
        api.info({
            icon: <FaCircleCheck className="text-blue-500 w-6 h-6 opacity-90"/>,
            message: <p className="text-base font-semibold text-gray-700">{"Copied to clipboard"}</p>,
            description: <p className="text-sm text-gray-500 break-keep uppercase">{value}</p>,
            placement: "topRight",
        });
    };

    const handleCopy = (key, value) => {
        navigator.clipboard.writeText(value).then(() => {
            setCopiedItem(key);
            openNotification(value);
        });
    };

    const KeyValueDisplay = ({label, value, keyName, handleCopy, copiedItem}) => {
        const renderValue = (value) => {
            if (typeof value === "boolean") {
                return value ? "Allowed" : "Not allowed";
            } else if (Array.isArray(value)) {
                if (keyName === "46A") {
                    return value.length > 0 ? (<ul className={'list-disc list-inside min-w-max'}>
                        {value.map((doc, index) => <li className={'h-6 mb-2 border-b'}
                                                       key={index}>{doc.fullInformation}</li>)}
                    </ul>) : "-";
                }
                if (keyName === "47A") {
                    return value.length > 0 ? (<ul className={'list-disc list-inside min-w-max'}>
                        {value.map((doc, index) => <li className={'h-6 mb-2 border-b'} key={index}>{doc}</li>)}
                    </ul>) : "-";
                }
                return value.join(", ");
            } else if (typeof value === "object" && value !== null) {
                return Object.entries(value).map(([key, val], index) => (
                    <div key={index}>
                        <strong>{key}:</strong> {val}
                    </div>
                ));
            }
            return (value || "-").replaceAll('\\n', '\n');
        };
        return (
            <div className={`text-xs pr-3`}>
                <div className="flex w-full items-center">
                    {keyName && (
                        <div
                            style={{whiteSpace: "pre-wrap"}}
                            className="px-2 w-9 h-full text-sky-600 font-medium text-xs flex-shrink-0"
                        >
                            {keyName}
                        </div>
                    )}

                    <div
                        className={`${
                            keyName ? "w-36" : "w-[11.2rem]"
                        } px-2 h-full text-sky-600 font-medium text-xs flex-shrink-0 flex items-center`}
                    >
                        {label}
                    </div>
                    <div className="w-full h-full py-4 px-2 border-l text-gray-600 whitespace-pre overflow-x-auto">
                        {renderValue(value)}
                    </div>
                    <button
                        className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100 transition flex-shrink-0"
                        onClick={() => handleCopy(keyName, value)}
                    >
                        {copiedItem === keyName ? (
                            <LuCopyCheck className="w-4 h-4 text-blue-500"/>
                        ) : (
                            <LuCopy className="w-4 h-4 text-gray-400"/>
                        )}
                    </button>
                </div>
            </div>
        );
    };

    const lcFields = [
        {
            key: "40A",
            label: "Form of Documentary Credit",
            value: letterOfCredit?.["40A"],
            text: "Form of Documentary Credit",
        },
        {
            key: "20",
            label: "Documentary Credit Number",
            value: letterOfCredit?.["20"],
            text: "Documentary Credit Number"
        },
        {key: "40E", label: "Applicable Rules", value: letterOfCredit?.["40E"], text: "Applicable Rules"},
        {key: "31C", label: "Date of Issue", value: letterOfCredit?.["31C"], text: "Date of Issue"},
        {
            key: "31D",
            label: "Date and Place of Expiry",
            value: letterOfCredit?.["31D"],
            text: "Date and Place of Expiry"
        },
        {key: "50", label: "Applicant Name", value: letterOfCredit?.["50"], text: "Applicant"},
        {key: "59", label: "Beneficiary Name", value: letterOfCredit?.["59"], text: "Beneficiary"},
        {key: "32B", label: "Currency Code Amount", value: letterOfCredit?.["32B"], text: "Currency Code Amount"},
        {
            key: "39A",
            label: "Percentage Credit Amount Tolerance",
            value: letterOfCredit?.["39A"],
            text: "Percentage Credit Amount Tolerance",
        },
        {key: "41A", label: "Available With", value: letterOfCredit?.["41A"], text: "Available With"},
        {key: "41D", label: "Available By", value: letterOfCredit?.["41D"], text: "Available By"},
        {key: "42C", label: "Drafts At", value: letterOfCredit?.["42C"], text: "Drafts At"},
        {key: "42A", label: "Drawee Bank", value: letterOfCredit?.["42A"], text: "Drawee Bank"},
        {key: "42D", label: "Drawee Bank", value: letterOfCredit?.["42D"], text: "Drawee Bank"},
        {key: "43P", label: "Partial Shipments", value: letterOfCredit?.["43P"], text: "Partial Shipments"},
        {key: "43T", label: "Transhipment", value: letterOfCredit?.["43T"], text: "Transhipment"},
        {key: "44E", label: "Port of Loading", value: letterOfCredit?.["44E"], text: "Port of Loading"},
        {key: "44F", label: "Port of Discharge", value: letterOfCredit?.["44F"], text: "Port of Discharge"},
        {
            key: "44B",
            label: "Place of Final Destination",
            value: letterOfCredit?.["44B"],
            text: "Place of Final Destination",
        },
        {key: "44C", label: "Latest Date of Shipment", value: letterOfCredit?.["44C"], text: "Latest Date of Shipment"},
        {key: "44D", label: "Shipment Period", value: letterOfCredit?.["44D"], text: "Shipment Period"},
        {key: "71D", label: "Charges Details", value: letterOfCredit?.["71D"], text: "Charges Details"},
        {key: "48", label: "Period for Presentation", value: letterOfCredit?.["48"], text: "Period for Presentation"},
        {
            key: "49",
            label: "Confirmation Instructions",
            value: letterOfCredit?.["49"],
            text: "Confirmation Instructions"
        },
        {
            key: "46A",
            label: "Documents Required",
            value: letterOfCredit?.["46A"],
            text: "Documents Required",
        },
        {key: "47A", label: "Additional Conditions", value: letterOfCredit?.["47A"], text: "Additional Conditions"},
    ];
    const lcAmendmentFields = [
        {key: "20", label: "Sender's Reference", value: letterOfCredit?.["20"], text: "Sender's Reference"},
        {key: "21", label: "Receiver Reference", value: letterOfCredit?.["21"], text: "Receiver's Reference"},
        {key: "27", label: "Sequence of Total", value: letterOfCredit?.["27"], text: "Sequence of Total"},
        {key: "30", label: "Date of Amendment", value: letterOfCredit?.["30"], text: "Date of Amendment"},
        {key: "31C", label: "Date of Issue", value: letterOfCredit?.["31C"], text: "Date of Issue"},
        {key: "26E", label: "Number of Amendment", value: letterOfCredit?.["26E"], text: "Number of Amendment"},
        {key: "22A", label: "Purpose of Message", value: letterOfCredit?.["22A"], text: "Purpose of Message"},
        {
            key: "32B",
            label: "Increase of Documentary Credit Amount",
            value: letterOfCredit?.["32B"],
            text: "Increase of Documentary Credit Amount"
        },
        {
            key: "33B",
            label: "Decrease of Documentary Credit Amount",
            value: letterOfCredit?.["33B"],
            text: "Decrease of Documentary Credit Amount"
        },
        {
            key: "34B",
            label: "New documentary credit amount after amendment",
            value: letterOfCredit?.["34B"],
            text: "New documentary credit amount after amendment"
        },
        {
            key: "39A",
            label: "Percentage Credit Amount Tolerance",
            value: letterOfCredit?.["39A"],
            text: "Percentage Credit Amount Tolerance"
        },
        {
            key: "39C",
            label: "Additional Amounts Covered",
            value: letterOfCredit?.["39C"],
            text: "Additional Amounts Covered"
        },
        {key: "31E", label: "Date of Expiry", value: letterOfCredit?.["31E"], text: "New Date of Expiry"},
        {key: "44A", label: "Place of Receipt", value: letterOfCredit?.["44A"], text: "Place of Receipt"},
        {key: "44B", label: "Place of Delivery", value: letterOfCredit?.["44B"], text: "Place of Delivery"},
        {key: "44C", label: "Latest Date of Shipment", value: letterOfCredit?.["44C"], text: "Latest Date of Shipment"},
        {key: "48", label: "Period for Presentation", value: letterOfCredit?.["48"], text: "Period for Presentation"},
        {key: "45B", label: "Description of Goods", value: letterOfCredit?.["45B"], text: "Description of Goods"},
        {key: "46B", label: "Documents Required", value: letterOfCredit?.["46B"], text: "Documents Required"},
        {key: "47B", label: "Additional Conditions", value: letterOfCredit?.["47B"], text: "Additional Conditions"},
        {
            key: "49",
            label: "Confirmation Instructions",
            value: letterOfCredit?.["49"],
            text: "Confirmation Instructions"
        },
        {key: "52A", label: "Issuing Bank", value: letterOfCredit?.["52A"], text: "Issuing Bank"},
        {key: "59", label: "Beneficiary", value: letterOfCredit?.["59"], text: "Beneficiary"},
        {
            key: "72",
            label: "Sender to Receiver Information",
            value: letterOfCredit?.["72"],
            text: "Sender to Receiver Information"
        },
        {key: "79", label: "Narrative", value: letterOfCredit?.["79"], text: "Narrative"},
    ]

    return (
        <div className="relative flex w-full">
            <div className="w-full pb-12">
                <div className="font-medium text-sky-600 uppercase text-xs py-2 border-b px-2 bg-sky-50">
                    LETTER OF CREDIT INFORMATION
                </div>

                <div className="divide-y w-full rounded-lg overflow-hidden">
                    {documentTypePath === 'letter_of_credit' ? lcFields.filter((field) => field.value).map((field) => (
                        <KeyValueDisplay
                            key={field.key}
                            label={`${field.label}`}
                            value={field.value}
                            keyName={field.key}
                            handleCopy={handleCopy}
                            copiedItem={copiedItem}
                        />
                    )) : lcAmendmentFields.filter((field) => field.value).map((field) => (
                        <KeyValueDisplay
                            key={field.key}
                            label={`${field.label}`}
                            value={field.value}
                            keyName={field.key}
                            handleCopy={handleCopy}
                            copiedItem={copiedItem}
                        />
                    ))}

                    {letterOfCredit?.["45A"] &&
                        <KeyValueDisplay
                            key={"45A"}
                            value={letterOfCredit["45A"]}
                            label={'45A DESCRIPTION OF GOODS'}
                            handleCopy={handleCopy}
                            copiedItem={copiedItem}
                        />
                    }
                </div>
            </div>
            {contextHolder}
        </div>
    );
};

export default ExtractLetterOfCreditResults;
